import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Layers, ChevronLeft, ChevronRight, RotateCw, Shuffle } from "lucide-react";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useViewerTracker } from "@/hooks/useViewerTracker";
import { renderMarkdownWithMath } from "@/lib/markdown-math";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";

type Flashcard = { question: string; answer: string; type: string };

/**
 * The nine stable `type` categories emitted by the Content Factory, in the
 * order they should appear in the filter row. `other` is the fallback for a
 * missing or unrecognised value. Labels match the content-side spec.
 */
const FLASHCARD_TYPES: { value: string; label: string }[] = [
  { value: "definition", label: "Definitions" },
  { value: "formula", label: "Formulas" },
  { value: "concept", label: "Concepts" },
  { value: "application", label: "Application" },
  { value: "brain-teaser", label: "Brain Teasers" },
  { value: "misconception", label: "Misconceptions" },
  { value: "reaction", label: "Reactions" },
  { value: "date-event", label: "Dates & Events" },
  { value: "other", label: "Other" },
];
const KNOWN_TYPES = new Set(FLASHCARD_TYPES.map((t) => t.value));

/** Missing / unknown `type` → "other"; otherwise the exact matched value. */
function normalizeType(v: unknown): string {
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    if (KNOWN_TYPES.has(t)) return t;
  }
  return "other";
}

export function FlashcardsPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Flashcards");
  const { reportEndReached } = useViewerTracker({
    chapterId,
    mode: "flashcards",
    active: paywall === null,
    minDwellSeconds: 60,
  });

  const { url, isLoading, isError, isLocked, isNotFound, refresh } = useSignedUrl({
    chapterId,
    assetType: "flashcards",
    enabled: shouldFetch,
  });

  const cards = useFlashcards(url);

  // Completion = every card in the deck revealed at least once. Flipping is a
  // deliberate act (unlike scrolling), and it's tracked by question text so the
  // category filter and shuffle can't reset or inflate the count.
  const revealedRef = useRef<Set<string>>(new Set());
  const handleCardRevealed = useCallback(
    (key: string) => {
      revealedRef.current.add(key);
      if (cards.cards.length > 0 && revealedRef.current.size >= cards.cards.length) {
        reportEndReached();
      }
    },
    [cards.cards.length, reportEndReached],
  );

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Flashcards"
      modeIcon={Layers}
      modeAccent="bg-yellow-500/10 text-yellow-600 dark:text-yellow-400"
    >
      {paywall ?? (
        isLocked ? (
          <PaywallCard modeLabel="Flashcards" classId={classId} />
        ) : isError ? (
          <GenericError onRetry={refresh} />
        ) : isLoading || cards.isLoading ? (
          <div className="flex-1 grid place-items-center text-sm text-text-muted">
            Preparing your flashcards…
          </div>
        ) : isNotFound || cards.cards.length === 0 ? (
          <EmptyDeck />
        ) : (
          <FlashcardDeck cards={cards.cards} onCardRevealed={handleCardRevealed} />
        )
      )}
    </ViewerScaffold>
  );
}

// --------------------------------------------------------------------------
function useFlashcards(url: string | null) {
  const query = useQuery({
    queryKey: ["flashcards", url ?? null],
    enabled: !!url,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Flashcard[]> => {
      const res = await fetch(url!);
      if (!res.ok) throw new Error(`Flashcards fetch failed: ${res.status}`);
      const text = await res.text();
      return parseFlashcards(text);
    },
    retry: 0,
  });
  return { cards: query.data ?? [], isLoading: !!url && query.isLoading, isError: query.isError };
}

/**
 * Format-agnostic flashcard parser. Content Factory decks ship as JSON
 * (`[{ front, back }]`); legacy decks are CSV (`question,answer`). We sniff
 * the payload rather than trust the asset's content_type, so a mislabelled
 * row never strands the viewer. JSON keys map onto the existing
 * `{ question, answer }` shape so LaTeX rendering is unchanged.
 */
function parseFlashcards(text: string): Flashcard[] {
  const head = text.trimStart();
  if (head.startsWith("[") || head.startsWith("{")) {
    try {
      return parseFlashcardsJson(JSON.parse(text));
    } catch {
      // Looked like JSON but didn't parse -- fall through to CSV.
    }
  }
  return parseCsv(text);
}

type RawCard = {
  front?: unknown;
  back?: unknown;
  question?: unknown;
  answer?: unknown;
  type?: unknown;
};

function toText(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (v == null) return "";
  return String(v).trim();
}

function parseFlashcardsJson(data: unknown): Flashcard[] {
  let arr: unknown[] = [];
  if (Array.isArray(data)) {
    arr = data;
  } else if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.cards)) arr = obj.cards;
    else if (Array.isArray(obj.flashcards)) arr = obj.flashcards;
  }
  return arr
    .map((raw) => {
      const c = (raw ?? {}) as RawCard;
      return {
        question: toText(c.front ?? c.question),
        answer: toText(c.back ?? c.answer),
        type: normalizeType(c.type),
      };
    })
    .filter((c) => c.question !== "" && c.answer !== "");
}

/**
 * Lightweight CSV parser that handles:
 *   - quoted fields with embedded commas / newlines
 *   - escaped quotes ("")
 *   - rows with > 2 columns (only the first two are used)
 *
 * Robust enough for the LMS content authoring format without pulling
 * in a dependency. NOT a general-purpose RFC 4180 parser.
 */
function parseCsv(text: string): Flashcard[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") {
        row.push(field); rows.push(row);
        row = []; field = "";
      } else if (c === "\r") { /* swallow */ }
      else field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }

  return rows
    .filter((r) => r.length >= 2 && r[0].trim() !== "" && r[1].trim() !== "")
    .map((r) => ({ question: r[0].trim(), answer: r.slice(1).join(",").trim(), type: "other" }))
    // Drop a "question,answer" header row if present.
    .filter((c, i) => !(i === 0 && /^questions?$/i.test(c.question) && /^answers?$/i.test(c.answer)));
}

// --------------------------------------------------------------------------
function FlashcardDeck({
  cards,
  onCardRevealed,
}: {
  cards: Flashcard[];
  onCardRevealed: (cardKey: string) => void;
}) {
  const [category, setCategory] = useState<string>("all");

  // Categories present in this deck, in canonical order, with counts. Driven
  // entirely by the data so different chapters surface different chips.
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of cards) counts.set(c.type, (counts.get(c.type) ?? 0) + 1);
    return FLASHCARD_TYPES.filter((t) => counts.has(t.value)).map((t) => ({
      ...t,
      count: counts.get(t.value)!,
    }));
  }, [cards]);

  // Only worth showing the filter when it actually partitions the deck.
  // (All-untyped decks collapse to a single "other" category → hidden.)
  const showFilter = categories.length >= 2;

  const filtered = useMemo(
    () => (category === "all" ? cards : cards.filter((c) => c.type === category)),
    [cards, category],
  );

  return (
    <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
      {showFilter ? (
        <div className="-mx-4 sm:-mx-6 px-4 sm:px-6 mb-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex gap-2 w-max">
            <FilterChip
              label="All"
              count={cards.length}
              active={category === "all"}
              onClick={() => setCategory("all")}
            />
            {categories.map((c) => (
              <FilterChip
                key={c.value}
                label={c.label}
                count={c.count}
                active={category === c.value}
                onClick={() => setCategory(c.value)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Remount on category change so deck order / position / flip reset. */}
      <DeckCarousel key={category} cards={filtered} onCardRevealed={onCardRevealed} />
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 h-8 text-xs font-medium transition-colors",
        active
          ? "border-brand bg-brand text-white"
          : "border-border bg-surface text-text-muted hover:bg-surface-2",
      )}
    >
      {label}
      <span
        className={cn(
          "tabular-nums text-[11px]",
          active ? "text-white/80" : "text-text-subtle",
        )}
      >
        {count}
      </span>
    </button>
  );
}

// --------------------------------------------------------------------------
function DeckCarousel({
  cards,
  onCardRevealed,
}: {
  cards: Flashcard[];
  onCardRevealed: (cardKey: string) => void;
}) {
  const [order, setOrder] = useState<number[]>(() => cards.map((_, i) => i));
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);

  // Reset when cards reload.
  useEffect(() => {
    setOrder(cards.map((_, i) => i));
    setIdx(0);
    setFlipped(false);
  }, [cards]);

  const total = order.length;
  const card = cards[order[idx]];

  const goNext = useCallback(() => {
    if (idx >= total - 1) return;
    setDirection(1);
    setIdx((i) => i + 1);
    setFlipped(false);
  }, [idx, total]);

  const goPrev = useCallback(() => {
    if (idx <= 0) return;
    setDirection(-1);
    setIdx((i) => i - 1);
    setFlipped(false);
  }, [idx]);

  const flip = useCallback(() => {
    // Only the question -> answer direction counts as revealing the card. Kept out
    // of the setState updater, which StrictMode may run twice.
    if (!flipped && card) onCardRevealed(card.question);
    setFlipped((f) => !f);
  }, [flipped, card, onCardRevealed]);

  const shuffle = useCallback(() => {
    setOrder((prev) => {
      const next = [...prev];
      // Fisher-Yates
      for (let i = next.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
      }
      return next;
    });
    setIdx(0);
    setFlipped(false);
  }, []);

  // Keyboard nav: Space/Enter to flip, Arrows to navigate.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if (e.key === " " || e.key === "Enter") { e.preventDefault(); flip(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
      else if (e.key === "ArrowLeft")  { e.preventDefault(); goPrev(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [flip, goNext, goPrev]);

  // Swipe handler -- framer-motion's drag with elasticity. Threshold of
  // 80px or 400 velocity advances/rewinds.
  const onDragEnd = (_: unknown, info: PanInfo) => {
    const { offset, velocity } = info;
    if (offset.x < -80 || velocity.x < -400) goNext();
    else if (offset.x > 80 || velocity.x > 400) goPrev();
  };

  const progressPct = ((idx + 1) / total) * 100;

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-text-muted tabular-nums">
          <span className="text-text font-medium">{idx + 1}</span>
          <span className="text-text-subtle"> / {total}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={shuffle} aria-label="Shuffle deck">
          <Shuffle className="h-3.5 w-3.5 mr-1.5" />
          Shuffle
        </Button>
      </div>

      <div className="relative" style={{ perspective: "1200px" }}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`${order[idx]}:${idx}`}
            custom={direction}
            initial={{ x: direction === 1 ? 60 : -60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction === 1 ? -60 : 60, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragEnd={onDragEnd}
            className="touch-pan-y"
          >
            <FlipCard front={card.question} back={card.answer} flipped={flipped} onFlip={flip} />
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="text-center text-[11px] sm:text-xs text-text-muted mt-3">
        <span className="sm:hidden">Tap to flip • Swipe left / right to navigate</span>
        <span className="hidden sm:inline">
          Click to flip • Space to flip • Arrows to navigate
        </span>
      </p>

      <div className="mt-5 grid grid-cols-3 gap-2">
        <Button
          variant="secondary"
          onClick={goPrev}
          disabled={idx === 0}
          aria-label="Previous card"
          className="h-11"
        >
          <ChevronLeft className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Previous</span>
        </Button>
        <Button
          onClick={flip}
          aria-label="Flip card"
          className="h-11"
        >
          <RotateCw className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Flip</span>
        </Button>
        <Button
          variant="secondary"
          onClick={goNext}
          disabled={idx === total - 1}
          aria-label="Next card"
          className="h-11"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4 sm:ml-1.5" />
        </Button>
      </div>

      <div className="mt-5">
        <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
          <motion.div
            className="h-full bg-brand"
            initial={false}
            animate={{ width: `${progressPct}%` }}
            transition={{ type: "spring", stiffness: 220, damping: 28 }}
          />
        </div>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
function FlipCard({
  front,
  back,
  flipped,
  onFlip,
}: {
  front: string;
  back: string;
  flipped: boolean;
  onFlip: () => void;
}) {
  const frontHtml = useMemo(() => renderMarkdownWithMath(front), [front]);
  const backHtml  = useMemo(() => renderMarkdownWithMath(back), [back]);

  return (
    <button
      type="button"
      onClick={onFlip}
      aria-label={flipped ? "Show question" : "Reveal answer"}
      className="block w-full text-left"
      style={{ transformStyle: "preserve-3d" }}
    >
      <motion.div
        className="relative w-full"
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
      >
        <CardFace label="Question" labelTone="brand" html={frontHtml} />
        <div
          className="absolute inset-0"
          style={{ transform: "rotateY(180deg)", backfaceVisibility: "hidden" }}
        >
          <CardFace label="Answer" labelTone="success" html={backHtml} />
        </div>
      </motion.div>
    </button>
  );
}

function CardFace({
  label,
  labelTone,
  html,
}: {
  label: string;
  labelTone: "brand" | "success";
  html: string;
}) {
  return (
    <div
      className={cn(
        "relative min-h-[280px] sm:min-h-[360px]",
        "rounded-2xl border border-border bg-surface shadow-sm",
        "px-5 sm:px-7 py-7 sm:py-9",
        "flex flex-col items-center justify-center text-center gap-4",
      )}
      style={{ backfaceVisibility: "hidden" }}
    >
      <span
        className={cn(
          "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-semibold",
          labelTone === "brand"
            ? "bg-brand-soft text-brand"
            : "bg-success/10 text-success",
        )}
      >
        {label}
      </span>
      <div
        className={cn(
          "text-base sm:text-xl leading-relaxed text-text",
          "[&_p]:my-1.5 [&_strong]:font-semibold",
          "[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc",
          "[&_.katex-display]:my-2",
        )}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <p className="text-[11px] sm:text-xs text-text-subtle inline-flex items-center gap-1">
        <RotateCw className="h-3 w-3" /> Tap to flip
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------
function EmptyDeck() {
  return (
    <div className="flex-1 grid place-items-center px-6 py-16 text-center text-text-muted">
      <div>
        <Layers className="mx-auto h-10 w-10 mb-3 opacity-60" />
        <p className="text-text font-medium">No flashcards yet</p>
        <p className="text-sm mt-1 max-w-xs mx-auto">
          The flashcard deck for this chapter is still in production. Check back soon.
        </p>
      </div>
    </div>
  );
}
